import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@mui/material";
import * as Yup from "yup";
import { debounce } from "lodash";
// @ts-expect-error: ignoring as it is existing js file
import { isValidInput } from "@components/Utils/helperFunction";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
// @ts-expect-error: ignoring as it is existing js file
import { NumericFormatCustom, currencyMaskedValue } from "@/utils/currencyMaskFormat";
import {
  updateLoanDetails,
  loanDetailsSelector,
  saveLoanDetails,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import { type ApplicationFormSlice, type LoanDetails } from "@/store/slices/types/applicationFormSlice";

const validationSchema = Yup.object({
  originationFee: Yup.string()
    .optional()
    .matches(regex.allowNumberWithZero, "Only numbers and decimal points allowed.")
    .matches(regex.allowNumberAndDecimal, {
      message: "Number must be up to 7 digits and up to 2 decimal places",
    })
    .max(10, "Maximum of 7 digits"),
});

const OriginationFee = () => {
  const dispatch = useDispatch();
  const { originationFee, _id } = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  const debouncedValidation = useMemo(
    () =>
      debounce(async (val: string) => {
        try {
          await validationSchema.validateAt("originationFee", { originationFee: val });
          setError("");
        } catch (err) {
          if (err instanceof Yup.ValidationError) {
            setError(err.message);
          }
        }
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedValidation.cancel();
    };
  }, [debouncedValidation]);

  const onChange = (val: string) => {
    if (val === "") {
      setValue(val);
      setError("");
      return;
    }
    if (!isValidInput(val)) {
      return;
    }
    setValue(val);
    debouncedValidation(val);
  };

  const onBlur = async (val: string) => {
    debouncedValidation.cancel();
    try {
      if (val !== "") {
        await validationSchema.validateAt("originationFee", { originationFee: val });
      }
      setError("");
      dispatch(saveLoanDetails({ originationFee: val }));
      if (_id && Number(val) !== Number(originationFee)) {
        dispatch(updateLoanDetails({ _id, originationFee: val }));
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setError(err.message);
      }
    }
  };

  useEffect(() => {
    setValue(String(originationFee || ""));
  }, [originationFee]);

  return (
    <TextField
      fullWidth
      id="originationFee"
      label="Origination Fee"
      variant="filled"
      type="text"
      size="small"
      name="originationFee"
      value={value}
      error={!!error}
      helperText={error}
      onChange={(event) => onChange(event?.target?.value)}
      onBlur={(event) => onBlur(currencyMaskedValue(event?.target?.value))}
      InputProps={{
        inputComponent: NumericFormatCustom,
      }}
    />
  );
};

export default OriginationFee;
